/**
 * Screen 02 «Сегодня» (salon-mockup.html today, redrawn 2026-10-02 from
 * concepts/today-final.html).
 *
 * Today and the next 6 days in one list, then «Дальше — в Календаре», which
 * opens day 8. Every visit is a card (concepts/today-approved.html, owner
 * 2026-10-03); finished visits fold into «Завершено»; the visit that is
 * starting carries «Пришёл / Не пришёл» from 15 min before, with a 5-second
 * «Отменить» (useMarks); unmarked visits group into one «Не отмечено» line;
 * the header reads «Сегодня · 4 700 / 13 000 сом». No trial banner and no
 * trial lock (owner, 2026-10-02): an expired salon is refused by the server
 * (402 SUBSCRIPTION_EXPIRED) when it saves.
 *
 * Landing (D-27, D-43, D-44): «Завершено» is row 1 and the highlighted card
 * follows; with nobody finished yet the screen opens on the highlighted card,
 * or the next visit. «Сейчас» appears when that visit is off
 * screen; while another day is on screen it reads «↑ Сегодня». The header
 * names the day whose band has reached the top of the list, and at the very
 * bottom the last day, because a short last day never reaches the top.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import type { AgendaItem } from '../components/Agenda';
import { Icon } from '../components/icons';
import { Shell } from '../components/Shell';
import { useSplashReady } from '../core/splash';
import { addDays, dayTitle } from '../lib/time';
import type { Appointment, DayView, Locale } from '../types';
import { dayShape, finishedSummary, firstDayAfter, headerDay, overlapsWith, todayBoard, visitCount, type Mark } from './today/model';
import {
  BreakLine,
  DayBand,
  FinishedFold,
  FreeLine,
  IncomeLine,
  MoreRow,
  UndoBar,
  UnmarkedLine,
  VisitCard,
  dayTag,
  useDaySum,
} from './today/rows';
import { useMarks } from './today/useMarks';
import { useSevenDays } from './today/useSevenDays';

function NoServices() {
  const { t } = useTranslation();
  return (
    <div className="card" style={{ margin: '14px 0' }}>
      <b className="nm" style={{ display: 'block', marginBottom: 4 }}>
        {t('today.noServicesTitle')}
      </b>
      <p className="sm" style={{ margin: '0 0 12px' }}>
        {t('today.noServicesText')}
      </p>
      <Link className="btn pri blk" to="/settings/services" style={{ textDecoration: 'none' }}>
        {t('today.noServicesButton')}
      </Link>
    </div>
  );
}

export function TodayScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language as Locale;
  const daySum = useDaySum();
  const { today: data, following, error } = useSevenDays();
  // Cold start (D-86): the splash leaves when the day has arrived, or failed to.
  useSplashReady(Boolean(data || error));

  const scroller = useRef<HTMLDivElement>(null);
  const landRow = useRef<HTMLDetailsElement | null>(null);
  const focusRow = useRef<HTMLDivElement | null>(null);
  const bands = useRef<(HTMLElement | null)[]>([]);
  const [focusOff, setFocusOff] = useState(false);
  const [shown, setShown] = useState(-1);

  const { marks, undoLabel, mark, undo } = useMarks();
  const board = data ? todayBoard(data, marks) : null;
  const firstVisit = board?.rows.find((r) => r.kind === 'visit');
  const focusId = board ? (board.highlight?.id ?? (firstVisit?.kind === 'visit' ? firstVisit.appt.id : null)) : null;

  const name = (a: Appointment) => a.client_name;
  const markOne = (a: Appointment, m: Mark) =>
    mark([a.id], m, `${name(a)}: ${t(m === 'completed' ? 'today.arrived' : 'today.noShow')}`);
  const markAll = () => {
    if (!board) return;
    const ids = board.unmarked.map((a) => a.id);
    mark(ids, 'completed', t('today.allMarked', { visits: t('today.visits', { count: ids.length }) }));
  };

  const goNow = useCallback((smooth: boolean) => {
    const box = scroller.current;
    if (!box) return;
    const row = landRow.current ?? focusRow.current;
    const top = row ? box.scrollTop + row.getBoundingClientRect().top - box.getBoundingClientRect().top - 4 : 0;
    box.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // Land once per load of the screen, not on every minute's refresh.
  const landed = useRef(false);
  useLayoutEffect(() => {
    if (data && !landed.current) {
      landed.current = true;
      goNow(false);
    }
  }, [data, goNow]);

  useEffect(() => {
    const box = scroller.current;
    const row = focusRow.current;
    if (!box || !row || typeof IntersectionObserver !== 'function') return;
    const io = new IntersectionObserver(([e]) => setFocusOff(!e.isIntersecting), { root: box, threshold: 0.6 });
    io.observe(row);
    return () => io.disconnect();
  }, [focusId, data]);

  const onScroll = () => {
    const box = scroller.current;
    if (!box) return;
    const top = box.getBoundingClientRect().top;
    const tops = bands.current.map((b) => (b ? b.getBoundingClientRect().top - top : Infinity));
    setShown(headerDay(tops, box.scrollTop + box.clientHeight >= box.scrollHeight - 2));
  };

  const open = (id: string) => navigate(`/appointments/${id}`);
  const book = (startsAt: string) => navigate(`/appointments/new?start=${encodeURIComponent(startsAt)}`);
  const focusRef = (id: string) => (el: HTMLDivElement | null) => {
    if (id === focusId) focusRow.current = el;
  };

  /** One row of any day; `sameDay` finds the overlaps, `live` is today's highlighted card. */
  const item = (it: AgendaItem, sameDay: Appointment[], live = false): ReactNode => {
    if (it.kind === 'break') return <BreakLine key={it.id} reason={it.reason} starts={it.at} ends={it.ends} />;
    if (it.kind === 'gap') return <FreeLine key={`gap-${it.at}`} gap={it.gap} onBook={() => book(it.gap.starts_at)} />;
    const hl = live && board?.highlight?.id === it.appt.id ? board.highlight : undefined;
    return (
      <VisitCard
        key={it.appt.id}
        ref={focusRef(it.appt.id)}
        appt={it.appt}
        overlaps={overlapsWith(it.appt, sameDay)}
        highlight={hl}
        arrivedLine={live}
        onOpen={() => open(it.appt.id)}
        onMark={(m) => markOne(it.appt, m)}
      />
    );
  };

  /** The line under a day's title, in the header and in the band. */
  const dayLine = (day: DayView): ReactNode => {
    const count = visitCount(day);
    if (count) return daySum(count, Number(day.expected_revenue));
    return day.working_hours ? t('today.noVisits') : t('today.dayOff');
  };

  const offOrEmpty = (day: DayView): ReactNode => {
    if (visitCount(day)) return null;
    if (!day.working_hours) return <div className="td-off">{t('today.dayOffClosed')}</div>;
    return <div className="td-off">{t('today.noVisits')}</div>;
  };

  // The header: today plain, any other day with a coloured badge.
  const shownDay = shown >= 0 ? following[shown] : data;
  const shownDate = data ? (shown >= 0 ? addDays(data.date, shown + 1) : data.date) : null;
  const title = shownDate ? dayTitle(shownDate, locale) : t('screen.today');
  const headLine =
    shown < 0 && data && board && visitCount(data) ? (
      <IncomeLine earned={board.earned} expected={board.expected} />
    ) : shownDay ? (
      dayLine(shownDay)
    ) : null;

  return (
    <Shell active="today" onTab={(tab) => tab === 'today' && goNow(true)}>
      <header className="appbar td-hd">
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="ttl ell">{title}</div>
          <div className="td-sum">
            {shownDate &&
              (shown < 0 ? (
                <>
                  <b>{t('nav.today')}</b> <span className="td-sep">·</span>{' '}
                </>
              ) : (
                <span className="td-tag">{shown === 0 ? t('today.tomorrow') : dayTag(shownDate, t)}</span>
              ))}
            {headLine}
          </div>
        </div>
        <button type="button" className="iconbtn" aria-label={t('book.title')} onClick={() => navigate('/appointments/new')}>
          <Icon.plus />
        </button>
      </header>
      <div className="content">
        <div className="pane pane-wide td-pane" style={{ padding: 0 }}>
          <div className="td-list" ref={scroller} onScroll={onScroll}>
            {data && board && (
              <>
                {!data.has_services && <NoServices />}
                <FinishedFold
                  ref={(el) => {
                    landRow.current = el;
                  }}
                  finished={board.finished}
                  summary={finishedSummary(board.finished)}
                  onOpen={open}
                />
                <UnmarkedLine visits={board.unmarked} onMark={markOne} onAll={markAll} />
                {!board.finished.length && !board.unmarked.length && offOrEmpty(data)}
                {board.rows.map((r) => item(r, data.appointments, true))}
                {following.map((day, i) => {
                  const date = addDays(data.date, i + 1);
                  const shape = day ? dayShape(day) : null;
                  return (
                    <div key={date}>
                      <DayBand
                        ref={(el) => {
                          bands.current[i] = el;
                        }}
                        day={date}
                        tomorrow={i === 0}
                        line={day && shape?.kind !== 'empty' ? dayLine(day) : undefined}
                      />
                      {day && offOrEmpty(day)}
                      {day && shape && shape.kind !== 'off' && shape.rows.map((r) => item(r, day.appointments))}
                    </div>
                  );
                })}
                <MoreRow onOpen={() => navigate(`/day/${firstDayAfter(data.date)}`)} />
              </>
            )}
          </div>
          <button type="button" className={`nowpill${(focusOff || shown >= 0) && !undoLabel ? ' on' : ''}`} onClick={() => goNow(true)}>
            {shown >= 0 ? t('today.backToToday') : t('today.now')}
          </button>
          <UndoBar label={undoLabel} onUndo={undo} />
        </div>
        <div className="pane pane-2">
          <span className="cap">
            {t('today.unread')} <span className="soon">{t('app.soon')}</span>
          </span>
          {/* The inbox is Phase 2 (D-40): the pane is drawn, the list is empty. */}
          <p className="xs">{data ? `${data.unread_count}` : ''}</p>
        </div>
      </div>
    </Shell>
  );
}
